import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router';
import { useClaimsStore } from '@/stores/claims-store';
import { useAuthStore } from '@/stores/auth-store';
import { EvidenceScore } from '@/components/claims/evidence-score';
import { PriorReportsSection } from '@/components/claims/prior-reports';
import { PurchasePackButton } from '@/components/claims/purchase-pack-button';
import { PackDownloadList } from '@/components/claims/pack-download-list';
import { ClaimTimeline } from '@/components/claims/claim-timeline';
import { OutcomeForm } from '@/components/claims/outcome-form';
import { ShareSuccess } from '@/components/claims/share-success';
import { api } from '@/lib/api';
import { clsx } from 'clsx';
import { useRealtimeRunsWithTag } from '@trigger.dev/react-hooks';
import type { CouncilInsight } from '@fixtyer/shared';
import { trackPackGenerated } from '@/lib/analytics';
import { uploadEvidenceWithMeasurement } from './evidence-upload-measurement';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB - matches the limit shown in the UI

// Document type display formatting
const formatDocumentType = (type: string): string => {
  const typeMap: Record<string, string> = {
    'pothole_photo': 'Pothole Photo',
    'damage_photo': 'Damage Photo',
    'repair_invoice': 'Repair Invoice',
    'repair_quote': 'Repair Quote',
    'dashcam': 'Dashcam Footage',
    'other': 'Document',
    'auto': 'Document',
  };
  return typeMap[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Format damage type for display (capitalize first letter)
const formatDamageType = (type: string): string => {
  const typeMap: Record<string, string> = {
    'tyre': 'Tyre',
    'wheel': 'Wheel',
    'suspension': 'Suspension',
    'bodywork': 'Bodywork',
    'steering': 'Steering',
    'other': 'Other',
  };
  return typeMap[type.toLowerCase()] || type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

const getDocumentIcon = (type: string): string => {
  const iconMap: Record<string, string> = {
    'pothole_photo': '🕳️',
    'damage_photo': '🚗',
    'repair_invoice': '🧾',
    'repair_quote': '📋',
    'dashcam': '📹',
    'other': '📄',
  };
  return iconMap[type] || '📄';
};

// Evidence categories with requirements and guidance
interface EvidenceCategory {
  id: string;
  name: string;
  icon: string;
  required: boolean;
  minCount: number;
  maxCount: number;
  points: number;
  description: string;
  tips: string[];
  docTypes: string[];
}

const EVIDENCE_CATEGORIES: EvidenceCategory[] = [
  {
    id: 'pothole',
    name: 'Pothole Photos',
    icon: '🕳️',
    required: true,
    minCount: 2,
    maxCount: 5,
    points: 30,
    description: 'Clear photos showing the pothole that caused your damage',
    tips: [
      'Include a ruler, coin, or 20p piece next to the pothole for scale',
      'Take a close-up showing the depth and edges',
      'Take a wider shot showing the road context and location',
      'If safe, photograph from your driving approach angle',
      'Potholes must be ≥40mm deep to meet the intervention threshold',
    ],
    docTypes: ['pothole_photo'],
  },
  {
    id: 'damage',
    name: 'Vehicle Damage Photos',
    icon: '🚗',
    required: true,
    minCount: 2,
    maxCount: 5,
    points: 25,
    description: 'Photos showing damage to your vehicle',
    tips: [
      'Photograph the damaged tyre showing puncture/bulge',
      'Capture any wheel rim damage (scratches, dents, cracks)',
      'Show any suspension or bodywork damage',
      'Include your vehicle registration plate in at least one photo',
      'Take photos before any repairs are made',
    ],
    docTypes: ['damage_photo'],
  },
  {
    id: 'financial',
    name: 'Repair Costs',
    icon: '🧾',
    required: true,
    minCount: 1,
    maxCount: 3,
    points: 25,
    description: 'Invoice or quote for repairs (1 required, 2+ strengthens claim)',
    tips: [
      'Get an itemised invoice showing parts and labour separately',
      'Include the garage name, address, and contact details',
      'If not yet repaired, get at least one written quote',
      'Keep receipts for any emergency repairs (e.g., tyre fitting)',
      'If claiming for a wheel, include proof of like-for-like replacement',
    ],
    docTypes: ['repair_invoice', 'repair_quote'],
  },
  {
    id: 'dashcam',
    name: 'Dashcam Footage',
    icon: '📹',
    required: false,
    minCount: 0,
    maxCount: 2,
    points: 15,
    description: 'Video showing the incident (highly valuable if available)',
    tips: [
      'Shows exact moment of impact — very strong evidence',
      'Proves you couldn\'t avoid the pothole',
      'Demonstrates road conditions and visibility',
      'Export the relevant clip (30 seconds before/after impact)',
    ],
    docTypes: ['dashcam'],
  },
  {
    id: 'other',
    name: 'Supporting Documents',
    icon: '📋',
    required: false,
    minCount: 0,
    maxCount: 5,
    points: 5,
    description: 'Any other relevant evidence',
    tips: [
      'Medical receipts if you suffered injury',
      'Recovery/tow truck receipt if vehicle was immobilised',
      'Witness statements with contact details',
      'Screenshot of Google Maps showing the location',
      'Previous FixMyStreet reports of the same pothole',
    ],
    docTypes: ['other'],
  },
];

interface EvidenceChecklistProps {
  documents: Array<{
    id: string;
    type: string;
    aiAnalysis?: any;
    aiProcessed?: boolean;
    rejected?: boolean;
  }>;
  onCategoryClick: (categoryId: string) => void;
  activeCategory: string | null;
}

function EvidenceChecklist({ documents, onCategoryClick, activeCategory }: EvidenceChecklistProps) {
  // Filter out rejected documents first
  const validDocuments = documents.filter(doc => {
    const analysis = doc.aiAnalysis as any;
    return !analysis?.rejected && !doc.rejected;
  });
  
  // Calculate what we have for each category
  const getCategoryStatus = (category: EvidenceCategory) => {
    const matchingDocs = validDocuments.filter(doc => {
      const detectedType = (doc.aiAnalysis as any)?.detectedType || doc.type;
      return category.docTypes.includes(detectedType);
    });
    
    const count = matchingDocs.length;
    const hasMinimum = count >= category.minCount;
    const isComplete = category.required ? hasMinimum : count > 0 || !category.required;
    const isOverLimit = count > category.maxCount;
    // Show progress as percentage of max uploads allowed (cap at 100%)
    const percentage = category.maxCount > 0 
      ? Math.min(100, (count / category.maxCount) * 100)
      : count > 0 ? 100 : 0;
    // Display count capped at max for cleaner UI
    const displayCount = Math.min(count, category.maxCount);
    
    return { count, displayCount, hasMinimum, isComplete, isOverLimit, percentage, docs: matchingDocs };
  };

  // Calculate overall progress
  const overallProgress = EVIDENCE_CATEGORIES.reduce((acc, cat) => {
    const status = getCategoryStatus(cat);
    const categoryWeight = cat.points;
    const categoryProgress = status.percentage * (categoryWeight / 100);
    return acc + categoryProgress;
  }, 0);

  const requiredComplete = EVIDENCE_CATEGORIES
    .filter(c => c.required)
    .every(c => getCategoryStatus(c).hasMinimum);

  return (
    <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
      {/* Header with progress */}
      <div className="p-6 border-b border-surface-200 bg-gradient-to-r from-brand-50 to-surface-50">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-semibold text-brand-950">
              Evidence Checklist
            </h2>
            <p className="text-sm text-surface-600 mt-1">
              Gather these items to build a strong claim
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-brand-600">{Math.round(overallProgress)}%</div>
            <div className="text-xs text-surface-500">complete</div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-3 bg-surface-200 rounded-full overflow-hidden">
          <div 
            className={clsx(
              "h-full rounded-full transition-all duration-500",
              overallProgress >= 80 ? "bg-success-500" :
              overallProgress >= 50 ? "bg-brand-500" :
              "bg-amber-500"
            )}
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        
        {/* Status message */}
        <p className={clsx(
          "text-sm mt-3 font-medium",
          requiredComplete ? "text-success-700" : "text-amber-700"
        )}>
          {requiredComplete 
            ? "✓ You have the essential evidence — add more to strengthen your claim!"
            : "⚠️ Some required evidence is missing — see highlighted items below"
          }
        </p>
      </div>

      {/* Category list */}
      <div className="divide-y divide-surface-100">
        {EVIDENCE_CATEGORIES.map((category, index) => {
          const status = getCategoryStatus(category);
          const isActive = activeCategory === category.id;
          
          return (
            <div key={category.id}>
              <button
                onClick={() => onCategoryClick(isActive ? '' : category.id)}
                className={clsx(
                  "w-full p-4 text-left transition-colors",
                  isActive ? "bg-brand-50" : "hover:bg-surface-50",
                  !status.isComplete && category.required && "bg-amber-50/50",
                  // Alternating subtle background for visual separation
                  !isActive && !(!status.isComplete && category.required) && index % 2 === 1 && "bg-surface-50/50"
                )}
              >
                <div className="flex items-center gap-4">
                  {/* Icon and status indicator */}
                  <div className={clsx(
                    "w-12 h-12 rounded-xl flex items-center justify-center text-2xl relative",
                    status.isComplete && status.count > 0 ? "bg-success-100" :
                    !status.isComplete && category.required ? "bg-amber-100" :
                    "bg-surface-100"
                  )}>
                    {category.icon}
                    {status.count > 0 && (
                      <span className={clsx(
                        "absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center text-white",
                        status.hasMinimum ? "bg-success-500" : "bg-amber-500"
                      )}>
                        {status.count}
                      </span>
                    )}
                  </div>
                  
                  {/* Category info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-brand-950">{category.name}</h3>
                      {category.required && (
                        <span className="text-xs bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded font-medium">
                          Required
                        </span>
                      )}
                      {!category.required && (
                        <span className="text-xs bg-surface-100 text-surface-600 px-1.5 py-0.5 rounded">
                          Optional
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-surface-600 truncate">{category.description}</p>
                    
                    {/* Mini progress */}
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 h-1.5 bg-surface-200 rounded-full overflow-hidden max-w-[120px]">
                        <div 
                          className={clsx(
                            "h-full rounded-full transition-all",
                            status.hasMinimum ? "bg-success-500" :
                            status.count > 0 ? "bg-amber-500" :
                            "bg-surface-300"
                          )}
                          style={{ width: `${status.percentage}%` }}
                        />
                      </div>
                      <span className={clsx(
                        "text-xs",
                        status.isOverLimit ? "text-amber-600 font-medium" : "text-surface-500"
                      )}>
                        {status.isOverLimit ? `${status.count}/${category.maxCount}` : `${status.displayCount}/${category.maxCount}`}
                        {status.isOverLimit && " ⚠️"}
                      </span>
                    </div>
                  </div>
                  
                  {/* Expand icon */}
                  <svg 
                    className={clsx(
                      "w-5 h-5 text-surface-400 transition-transform",
                      isActive && "rotate-180"
                    )} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {/* Expanded tips */}
              {isActive && (
                <div className="px-4 pb-4 bg-brand-50/50">
                  <div className="ml-16 space-y-3">
                    {/* Tips */}
                    <div className="bg-white rounded-lg p-4 border border-brand-100">
                      <p className="text-sm font-medium text-brand-800 mb-2">
                        💡 Tips for strong evidence:
                      </p>
                      <ul className="space-y-1.5">
                        {category.tips.map((tip, i) => (
                          <li key={i} className="text-sm text-surface-700 flex items-start gap-2">
                            <span className="text-brand-400 mt-0.5">•</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Existing documents in this category */}
                    {status.docs.length > 0 && (
                      <div className="bg-white rounded-lg p-3 border border-success-100">
                        <p className="text-xs font-medium text-success-700 mb-2">
                          ✓ You've uploaded:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {status.docs.map(doc => (
                            <span key={doc.id} className="text-xs bg-success-50 text-success-700 px-2 py-1 rounded">
                              {(doc.aiAnalysis as any)?.extracted?.description?.substring(0, 30) || 'Document'}{(doc.aiAnalysis as any)?.extracted?.description?.length > 30 ? '...' : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* What's missing */}
                    {!status.hasMinimum && category.required && (
                      <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                        <p className="text-sm text-amber-800">
                          <span className="font-medium">⚠️ Missing:</span> Add {category.minCount - status.count} more {category.name.toLowerCase()} to meet the minimum requirement.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface HighwayAuthorityCardProps {
  claim: {
    id: string;
    locationPostcode?: string | null;
    councilInsights?: CouncilInsight | null;
    authorityAddress?: string | null;
    authority?: {
      id: string;
      name: string;
      type?: string;
      region?: string | null;
      contactEmail?: string | null;
      contactAddress?: string | null;
      claimsPortalUrl?: string | null;
      phone?: string | null;
      typicalResponseDays?: number | null;
      historicalSuccessRate?: number | null;
      // DfT ratings
      dftOverallRating?: 'green' | 'amber' | 'red' | null;
      dftConditionRating?: 'green' | 'amber' | 'red' | null;
      dftSpendRating?: 'green' | 'amber' | 'red' | null;
      dftBestPracticeRating?: 'green' | 'amber' | 'red' | null;
      dftRatingYear?: string | null;
    } | null;
  };
  onIdentify: () => Promise<unknown>;
  onUpdateAddress: (address: string) => Promise<void>;
  readOnly?: boolean;
}

// Rating badge component
function DftRatingBadge({ rating, label }: { rating: 'green' | 'amber' | 'red' | null; label?: string }) {
  if (!rating) return null;
  
  const styles = {
    green: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    red: 'bg-rose-100 text-rose-800 border-rose-200',
  };
  
  const labels = { green: 'Green', amber: 'Amber', red: 'Red' };
  
  return (
    <span className={clsx(
      'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded border',
      styles[rating]
    )}>
      {label && <span className="font-normal opacity-75">{label}:</span>}
      {labels[rating]}
    </span>
  );
}

// Get claim strength messaging based on rating
function getClaimStrengthInfo(rating: 'green' | 'amber' | 'red' | null): {
  icon: string;
  title: string;
  message: string;
  bgColor: string;
  borderColor: string;
} {
  switch (rating) {
    case 'red':
      return {
        icon: '💪',
        title: 'Strong position for your claim',
        message: 'This council is rated RED by the Department for Transport — they have significant issues with road maintenance. This strengthens your case as it undermines the "we didn\'t know" defence.',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
      };
    case 'amber':
      return {
        icon: '📊',
        title: 'Reasonable position for your claim',
        message: 'This council is rated AMBER — there\'s room for improvement in their road maintenance. This provides supporting context for your claim.',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
      };
    case 'green':
      return {
        icon: 'ℹ️',
        title: 'Council rated well for maintenance',
        message: 'This council is rated GREEN for road maintenance. You\'ll need strong evidence of the specific defect, but this doesn\'t prevent a successful claim.',
        bgColor: 'bg-surface-50',
        borderColor: 'border-surface-200',
      };
    default:
      return {
        icon: '📋',
        title: 'No DfT rating available',
        message: 'This authority isn\'t covered by the DfT rating system. Your claim will be assessed on its own merits.',
        bgColor: 'bg-surface-50',
        borderColor: 'border-surface-200',
      };
  }
}

function HighwayAuthorityCard({ claim, onIdentify, onUpdateAddress, readOnly = false }: HighwayAuthorityCardProps) {
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [addressValue, setAddressValue] = useState(claim.authorityAddress || claim.authority?.contactAddress || '');
  
  const handleIdentify = async () => {
    setIsIdentifying(true);
    try {
      await onIdentify();
    } finally {
      setIsIdentifying(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!addressValue.trim()) return;
    setIsSavingAddress(true);
    try {
      await onUpdateAddress(addressValue.trim());
      setIsEditingAddress(false);
    } finally {
      setIsSavingAddress(false);
    }
  };

  // Determine the current address to display
  const currentAddress = claim.authorityAddress || claim.authority?.contactAddress;

  // No postcode - can't identify
  if (!claim.locationPostcode) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <h3 className="font-display text-lg font-semibold text-brand-950 mb-3">
          Highway Authority
        </h3>
        <div className="text-sm text-surface-600">
          <p className="mb-2">Add a postcode to your claim to identify the authority likely to be responsible for maintaining the road.</p>
          <p className="text-xs text-surface-500">This is who you'll send your claim to.</p>
        </div>
      </div>
    );
  }

  // Has authority linked
  if (claim.authority) {
    // Use councilInsights rating if available, otherwise fall back to authority rating
    const effectiveRating = claim.councilInsights?.rating || claim.authority.dftOverallRating || null;
    const strengthInfo = getClaimStrengthInfo(effectiveRating);
    // Show DfT badge section only if authority has the badge data
    const hasAuthorityRatingBadges = claim.authority.dftOverallRating != null;
    
    return (
      <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-surface-100">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-success-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="font-display text-base font-semibold text-brand-950">
                {claim.authority.name}
              </h3>
              <p className="text-sm text-surface-600 mt-0.5">
                Responsible for this road
              </p>
            </div>
          </div>
        </div>

        {/* Council Address Section */}
        <div className="px-4 py-3 bg-surface-50 border-b border-surface-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-surface-500 uppercase tracking-wide">
              📮 Responsible Body Postal Address
            </span>
            {currentAddress && !isEditingAddress && !readOnly && (
              <button
                onClick={() => setIsEditingAddress(true)}
                className="text-xs text-brand-600 hover:underline"
              >
                Edit
              </button>
            )}
          </div>
          
          {(isEditingAddress || !currentAddress) && !readOnly ? (
            <div className="space-y-2">
              <textarea
                value={addressValue}
                onChange={(e) => setAddressValue(e.target.value)}
                placeholder={`e.g.\nClaims Department\n${claim.authority.name}\nTown Hall\nHigh Street\nCity\nPostcode`}
                rows={5}
                className="w-full px-3 py-2 text-sm border border-surface-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
              />
              <p className="text-xs text-surface-500">
                💡 You can usually find this on the council's website under "Contact Us" or their highways/roads section.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveAddress}
                  disabled={isSavingAddress || !addressValue.trim()}
                  className={clsx(
                    "flex-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors",
                    isSavingAddress || !addressValue.trim()
                      ? "bg-surface-100 text-surface-400 cursor-not-allowed"
                      : "bg-brand-600 text-white hover:bg-brand-700"
                  )}
                >
                  {isSavingAddress ? 'Saving...' : 'Save Address'}
                </button>
                {currentAddress && (
                  <button
                    onClick={() => {
                      setIsEditingAddress(false);
                      setAddressValue(currentAddress);
                    }}
                    className="px-3 py-1.5 text-sm text-surface-600 hover:text-surface-800"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-surface-700 whitespace-pre-line">
              {currentAddress}
            </div>
          )}

          {!currentAddress && !isEditingAddress && !readOnly && (
            <button
              onClick={() => setIsEditingAddress(true)}
              className="w-full py-2 text-sm text-brand-600 hover:text-brand-700 border border-dashed border-surface-300 rounded-lg hover:border-brand-300 transition-colors"
            >
              + Add council address for your claim letter
            </button>
          )}
        </div>
        
        {/* DfT Rating Section - only show badges if authority has them */}
        {hasAuthorityRatingBadges && (
          <div className="p-4 bg-surface-50 border-b border-surface-100">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-surface-500 uppercase tracking-wide">
                DfT Road Maintenance Rating {claim.authority.dftRatingYear}
              </span>
              <Link 
                to="/authority-ratings" 
                className="text-xs text-brand-600 hover:underline"
              >
                View all →
              </Link>
            </div>
            <div className="flex flex-wrap gap-2">
              <DftRatingBadge rating={claim.authority.dftOverallRating || null} label="Overall" />
              {claim.authority.dftConditionRating && (
                <DftRatingBadge rating={claim.authority.dftConditionRating} label="Condition" />
              )}
              {claim.authority.dftSpendRating && (
                <DftRatingBadge rating={claim.authority.dftSpendRating} label="Spend" />
              )}
            </div>
          </div>
        )}
        
        {/* Claim Strength Indicator */}
        <div className={clsx('p-4', strengthInfo.bgColor, 'border-b', strengthInfo.borderColor)}>
          <div className="flex items-start gap-3">
            <span className="text-xl flex-shrink-0">{strengthInfo.icon}</span>
            <div>
              <p className="font-medium text-sm text-brand-950">
                {strengthInfo.title}
              </p>
              <p className="text-xs text-surface-700 mt-1 leading-relaxed">
                {strengthInfo.message}
              </p>
            </div>
          </div>
        </div>
        
        {/* Council Insights Section - If we have detailed data */}
        {claim.councilInsights && (
          <div className="p-4 bg-white border-b border-surface-100">
            <div className="flex items-start gap-3">
              <div className={clsx(
                'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold',
                claim.councilInsights.rating === 'red' ? 'bg-rose-100 text-rose-700' :
                claim.councilInsights.rating === 'amber' ? 'bg-amber-100 text-amber-700' :
                'bg-emerald-100 text-emerald-700'
              )}>
                {claim.councilInsights.rating === 'red' ? '!' : claim.councilInsights.rating === 'amber' ? '~' : '✓'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-brand-950">
                  {claim.councilInsights.headline}
                </p>
                <p className="text-xs text-surface-600 mt-2 leading-relaxed">
                  {claim.councilInsights.summary}
                </p>
                
                {/* Key Facts */}
                <ul className="mt-3 space-y-1">
                  {claim.councilInsights.keyFacts.slice(0, 3).map((fact, i) => (
                    <li key={i} className="text-xs text-surface-600 flex items-start gap-2">
                      <span className="text-brand-500 mt-0.5">•</span>
                      <span>{fact}</span>
                    </li>
                  ))}
                </ul>
                
                {/* Links */}
                <div className="flex flex-wrap gap-3 mt-3">
                  <Link 
                    to={claim.councilInsights.pageUrl}
                    className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
                  >
                    Full report →
                  </Link>
                  {claim.councilInsights.transparencyReportUrl && (
                    <a 
                      href={claim.councilInsights.transparencyReportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-surface-500 hover:text-surface-700 inline-flex items-center gap-1"
                    >
                      Council source ↗
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Footer info */}
        <div className="px-6 py-4 text-xs text-surface-500">
          {claim.councilInsights 
            ? "Mac includes this council analysis in your claim pack to strengthen your Section 58 rebuttal."
            : "Where DfT road condition rating data is available for your location, Mac includes this in your claim pack."}
        </div>
      </div>
    );
  }

  // Has postcode but no authority identified
  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-6">
      <h3 className="font-display text-lg font-semibold text-brand-950 mb-3">
        Highway Authority
      </h3>
      
      <div className="space-y-3">
        <p className="text-sm text-surface-600">
          We need to identify the authority likely to be responsible for maintaining this road so your claim goes to the right place.
        </p>
        
        <button
          onClick={handleIdentify}
          disabled={isIdentifying}
          className={clsx(
            "w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors",
            isIdentifying
              ? "bg-surface-100 text-surface-400 cursor-not-allowed"
              : "bg-brand-600 text-white hover:bg-brand-700"
          )}
        >
          {isIdentifying ? (
            <>
              <span className="animate-spin">⏳</span>
              Looking up authority...
            </>
          ) : (
            <>
              <span>🔍</span>
              Find responsible authority
            </>
          )}
        </button>

        <p className="text-xs text-surface-500 text-center">
          Based on postcode: <strong>{claim.locationPostcode}</strong>
        </p>
      </div>
    </div>
  );
}

// Incident Description Card with AI feedback and edit capability
interface IncidentDescriptionCardProps {
  claimId: string;
  description: string;
  damageTypes?: string[];
  incidentDate: string;
  cachedAnalysis?: DescriptionAnalysis | null;
  onUpdate: (newDescription: string) => Promise<void>;
  readOnly?: boolean;
}

interface DescriptionAnalysis {
  score: number;
  isStrong: boolean;
  summary: string;
  strengths: string[];
  improvements: string[];
  missingElements: string[];
  aiPowered: boolean;
}

function IncidentDescriptionCard({ claimId, description, damageTypes, incidentDate, onUpdate, cachedAnalysis, readOnly = false }: IncidentDescriptionCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(description);
  const [isSaving, setIsSaving] = useState(false);
  const [analysis, setAnalysis] = useState<DescriptionAnalysis | null>(cachedAnalysis || null);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const accessToken = useAuthStore((state) => state.accessToken);
  
  // Update analysis when cached analysis prop changes (e.g., after claim refresh)
  useEffect(() => {
    if (cachedAnalysis) {
      setAnalysis(cachedAnalysis);
    }
  }, [cachedAnalysis]);
  
  // Sync edit value when description changes from parent (e.g., after AI improvement)
  useEffect(() => {
    setEditValue(description);
  }, [description]);
  
  // Run analysis on mount if no cached analysis exists (first time viewing)
  useEffect(() => {
    if (description && claimId && accessToken && !cachedAnalysis && !analysis && !isAnalysing) {
      runAnalysis(description, false);
    }
  }, [claimId, description, accessToken, cachedAnalysis]);
  
  // Run AI analysis - only called explicitly after saving
  const runAnalysis = async (textToAnalyse: string, forceReanalyse = false) => {
    setIsAnalysing(true);
    try {
      const data = await api.post<DescriptionAnalysis>(
        `/api/claims/${claimId}/analyse-description`,
        { description: textToAnalyse, forceReanalyse }
      );
      setAnalysis(data);
      return data;
    } catch {
      // Analysis failed - non-critical
    } finally {
      setIsAnalysing(false);
    }
    return null;
  };
  
  const handleSave = async () => {
    if (!editValue.trim() || editValue === description) {
      setIsEditing(false);
      setEditValue(description);
      return;
    }
    
    setIsSaving(true);
    try {
      await onUpdate(editValue);
      setIsEditing(false);
      
      // Only run AI analysis on the FIRST save (when no analysis exists yet)
      // Subsequent edits just save the text - AI will run again during pack generation
      const hasExistingAnalysis = !!(analysis || cachedAnalysis);
      if (!hasExistingAnalysis) {
        await runAnalysis(editValue, false);
      }
    } catch {
      // Save failed - non-critical
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCancel = () => {
    setEditValue(description);
    setIsEditing(false);
  };

  // Determine border color based on analysis
  const getBorderColor = () => {
    if (!analysis) return 'border-surface-200';
    if (analysis.isStrong) return 'border-success-200';
    return 'border-amber-200';
  };
  
  return (
    <div className={clsx("bg-white rounded-2xl border p-6", getBorderColor())}>
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-display text-lg font-semibold text-brand-950">
          What Happened
        </h3>
        <div className="flex items-center gap-2">
          {isAnalysing && (
            <span className="text-xs text-surface-500 flex items-center gap-1.5">
              <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Mac is checking...
            </span>
          )}
          {!isEditing && !isAnalysing && analysis && (
            <>
              {analysis.isStrong ? (
                <span 
                  className="px-2 py-0.5 bg-success-100 text-success-700 text-xs font-medium rounded flex items-center gap-1 cursor-help"
                  title="Description Quality Score: Measures how clear, detailed, and well-written your incident description is. Strong descriptions include specific details about the incident, damage, and circumstances."
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Strong ({analysis.score}/100)
                </span>
              ) : (
                <span 
                  className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded cursor-help"
                  title="Description Quality Score: Measures how clear, detailed, and well-written your incident description is. Consider adding more specific details about the incident, damage, and circumstances to improve this score."
                >
                  {analysis.score}/100
                </span>
              )}
            </>
          )}
          {!isEditing && !readOnly && (
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 text-surface-500 hover:text-brand-600 hover:bg-surface-100 rounded-lg transition-colors"
              title="Edit description"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {/* The description - view or edit mode */}
      {isEditing ? (
        <div className="space-y-3">
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            rows={5}
            className="w-full px-4 py-3 rounded-xl border border-surface-300 bg-white text-brand-950 placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors resize-none"
            placeholder="Describe exactly what happened..."
            autoFocus
          />
          
          {/* Show current analysis tips while editing */}
          {analysis && !analysis.isStrong && analysis.improvements.length > 0 && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs font-medium text-amber-800 mb-1.5">💡 Tips to strengthen:</p>
              <ul className="space-y-1">
                {analysis.improvements.slice(0, 3).map((tip, i) => (
                  <li key={i} className="text-xs text-amber-700">{tip}</li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-surface-700 hover:text-surface-900 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !editValue.trim()}
              className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Reassuring guidance */}
          <div className="p-3 bg-brand-50 border border-brand-100 rounded-xl mb-4">
            <p className="text-sm text-brand-800">
              <span className="font-medium">💡 Don't worry about making this sound polished.</span>{' '}
              Just capture everything you remember — the sights, sounds, and what happened before, during, and after. 
              Mac will help you strengthen it for your claim.
            </p>
          </div>
          
          {/* The description text */}
          <div className="p-4 bg-surface-50 rounded-xl mb-4">
            <p className="text-surface-800 leading-relaxed">"{description}"</p>
            <div className="flex items-center gap-3 mt-3 text-xs text-surface-500">
              <span>📅 {new Date(incidentDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              {damageTypes && damageTypes.length > 0 && (
                <span>🔧 {damageTypes.map(formatDamageType).join(', ')}</span>
              )}
            </div>
          </div>
          
          {/* AI Analysis Results */}
          {analysis && (
            <div className="space-y-4">
              {/* Summary */}
              <p className="text-sm text-surface-700">{analysis.summary}</p>
              
              {/* Strengths */}
              {analysis.strengths.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-success-700 flex items-center gap-1.5 mb-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    What's good
                  </p>
                  <ul className="space-y-1">
                    {analysis.strengths.map((strength, i) => (
                      <li key={i} className="text-sm text-surface-600 pl-6">• {strength}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Improvements needed */}
              {analysis.improvements.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-amber-700 flex items-center gap-1.5 mb-2">
                    <span>💡</span>
                    To strengthen your claim:
                  </p>
                  <ul className="space-y-2">
                    {analysis.improvements.map((improvement, i) => (
                      <li key={i} className="text-sm text-surface-700 flex items-start gap-2 pl-6">
                        <span className="text-amber-500 mt-0.5">→</span>
                        {improvement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Missing elements */}
              {analysis.missingElements.length > 0 && (
                <div className="p-3 bg-surface-50 rounded-lg border border-surface-200">
                  <p className="text-xs font-medium text-surface-600 mb-1.5">Consider adding:</p>
                  <div className="flex flex-wrap gap-2">
                    {analysis.missingElements.map((element, i) => (
                      <span key={i} className="text-xs bg-white px-2 py-1 rounded border border-surface-200 text-surface-600">
                        {element}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action buttons */}
              {!readOnly && (
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  {/* Edit button */}
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-sm font-medium text-surface-600 hover:text-brand-600 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit manually
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ClaimDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    currentClaim, 
    documents,
    fetchDocuments,
    identifyAuthority,
    getRealtimeToken,
    priorReports,
    packs,
    isPackGenerating,
    setPackGenerating,
    fetchPacks,
    isLoading,
    error: claimError,
    fetchClaim, 
    updateClaim,
    uploadDocument, 
    deleteDocument,
    searchPriorReports,
    confirmPriorReport,
    removePriorReport,
    clearCurrentClaim 
  } = useClaimsStore();
  
  // Claim becomes read-only once a pack has been generated
  const isPackGenerated = packs.length > 0;

  useEffect(() => {
    for (const pack of packs) {
      if (pack.packType === 'council' || pack.packType === 'court') {
        trackPackGenerated(pack.packType, currentClaim?.id ?? pack.packType);
      }
    }
  }, [packs, currentClaim?.id]);
  
  const [isUploading, setIsUploading] = useState(false);
  const [isSearchingReports, setIsSearchingReports] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingUploads, setPendingUploads] = useState<Array<{
    tempId: string;
    fileName: string;
    isImage: boolean;
    previewUrl?: string;
    status: 'uploading' | 'error';
    error?: string;
  }>>([]);

  // Keep a live ref so we can revoke any leftover preview object URLs on unmount.
  const pendingUploadsRef = useRef(pendingUploads);
  pendingUploadsRef.current = pendingUploads;
  useEffect(() => () => {
    pendingUploadsRef.current.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
  }, []);
  const [paymentMessage, setPaymentMessage] = useState<{ type: 'success' | 'cancelled' | null; text: string } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [activeEvidenceCategory, setActiveEvidenceCategory] = useState<string | null>(null);
  
  // Over-limit modal state
  const [overLimitModal, setOverLimitModal] = useState<{
    show: boolean;
    category: string;
    categoryName: string;
    maxCount: number;
    docToDelete: string | null;
  } | null>(null);
  const [isDeletingOverLimit, setIsDeletingOverLimit] = useState(false);
  const dismissedOverLimitDocsRef = useRef<Set<string>>(new Set());
  
  // Realtime subscription state
  const [realtimeToken, setRealtimeToken] = useState<string | null>(null);
  const [claimTag, setClaimTag] = useState<string | null>(null);
  const lastCompletedRunRef = useRef<string | null>(null);

  useEffect(() => {
    if (id) {
      // fetchClaim also fetches documents, prior reports, and packs
      fetchClaim(id);
    }
    return () => clearCurrentClaim();
  }, [id, fetchClaim, clearCurrentClaim]);

  // Auto-identify authority if claim has location but no authority yet
  const [authorityIdentified, setAuthorityIdentified] = useState(false);
  const [insightsRefreshed, setInsightsRefreshed] = useState(false);
  
  useEffect(() => {
    if (!currentClaim || authorityIdentified) return;
    
    const hasLocation = currentClaim.locationLat && currentClaim.locationLng;
    const needsAuthority = !currentClaim.authority && !currentClaim.authorityId;
    
    if (hasLocation && needsAuthority) {
      identifyAuthority(currentClaim.id)
        .then(() => {
          setAuthorityIdentified(true);
          // Refresh claim to get updated authority data including DfT rating
          fetchClaim(currentClaim.id);
        })
        .catch(() => {
          setAuthorityIdentified(true); // Don't retry
        });
    } else if (currentClaim.authority) {
      setAuthorityIdentified(true); // Already has authority
    }
  }, [currentClaim?.id, currentClaim?.locationLat, currentClaim?.authority, authorityIdentified, identifyAuthority, fetchClaim]);

  // Auto-refresh council insights if claim has authority but missing insights
  useEffect(() => {
    if (!currentClaim || insightsRefreshed) return;
    
    const hasAuthority = !!currentClaim.authority;
    const missingInsights = !currentClaim.councilInsights;
    
    if (hasAuthority && missingInsights) {
      setInsightsRefreshed(true); // Mark as attempted immediately to prevent re-runs
      
      api.post(`/api/claims/${currentClaim.id}/refresh-council-insights`)
        .then(() => {
          // Refresh claim to get updated data
          fetchClaim(currentClaim.id);
        })
        .catch(() => {
          // Non-critical - insights refresh failed
        });
    } else if (currentClaim.councilInsights) {
      setInsightsRefreshed(true); // Already has insights
    }
  }, [currentClaim?.id, currentClaim?.authority, currentClaim?.councilInsights, insightsRefreshed, fetchClaim]);

  // Poll for prior reports after page load (Trigger.dev check-prior-reports runs async)
  const { fetchPriorReports } = useClaimsStore();
  const [priorReportsPollStarted, setPriorReportsPollStarted] = useState(false);
  
  useEffect(() => {
    if (!id || !currentClaim) return;
    if (priorReportsPollStarted) return; // Only poll once per page load
    
    const hasLocation = currentClaim.locationLat && currentClaim.locationLng;
    
    // Start polling if we have a location (Trigger.dev task may still be running)
    if (hasLocation && priorReports.length === 0) {
      setPriorReportsPollStarted(true);
      
      let attempts = 0;
      const maxAttempts = 15; // Poll for up to 30 seconds
      
      const pollInterval = setInterval(async () => {
        attempts++;
        
        try {
          await fetchPriorReports(id);
          
          // Check if we found reports or max attempts reached
          const currentReports = useClaimsStore.getState().priorReports;
          if (currentReports.length > 0) {
            clearInterval(pollInterval);
            
            // Refresh claim to get updated evidence score (backend triggers re-analysis)
            setTimeout(() => fetchClaim(id), 3000); // Wait for backend analysis
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
          }
        } catch {
          // Non-critical poll error
        }
      }, 2000); // Poll every 2 seconds
      
      return () => clearInterval(pollInterval);
    }
  }, [id, currentClaim?.id, currentClaim?.locationLat, priorReports.length, fetchPriorReports, priorReportsPollStarted]);

  // Handle payment redirect from Stripe
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    
    if (paymentStatus === 'success') {
      setPaymentMessage({
        type: 'success',
        text: 'Payment successful! Your claim pack is being generated. You\'ll receive an email when it\'s ready.',
      });
      setPackGenerating(true);
      
      // Clear the payment param from URL
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
      
      // Start polling for pack completion
      if (id) {
        const pollInterval = setInterval(() => {
          fetchPacks(id).then(() => {
            // Check if packs have been generated
            const store = useClaimsStore.getState();
            if (store.packs.length > 0) {
              setPackGenerating(false);
              clearInterval(pollInterval);
            }
          });
        }, 5000);
        
        // Stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setPackGenerating(false);
        }, 300000);
        
        return () => clearInterval(pollInterval);
      }
    } else if (paymentStatus === 'cancelled') {
      setPaymentMessage({
        type: 'cancelled',
        text: 'Payment was cancelled. You can try again when you\'re ready.',
      });
      
      // Clear the payment param from URL
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, id, fetchPacks, setPackGenerating]);

  // Get realtime token when we have pending documents
  const pendingDocs = documents.filter((doc) => !doc.aiProcessed);
  const hasPendingDocs = pendingDocs.length > 0;
  
  useEffect(() => {
    if (!id || !hasPendingDocs || realtimeToken) return;
    
    // Get a realtime token for this claim
    getRealtimeToken(id)
      .then((data) => {
        setRealtimeToken(data.publicToken);
        setClaimTag(data.claimTag);
      })
      .catch(() => {
        // Fall back to polling if realtime token fails
      });
  }, [id, hasPendingDocs, realtimeToken, getRealtimeToken]);

  // Subscribe to realtime updates for this claim's runs
  const { runs: realtimeRuns } = useRealtimeRunsWithTag(
    claimTag || 'none',
    { 
      accessToken: realtimeToken || undefined,
      enabled: !!realtimeToken && !!claimTag,
    }
  );

  // When a run completes, refresh ALL relevant data (not just documents)
  useEffect(() => {
    if (!realtimeRuns || realtimeRuns.length === 0) return;
    
    const completedRun = realtimeRuns.find(
      (run) => run.status === 'COMPLETED' && run.id !== lastCompletedRunRef.current
    );
    
    if (completedRun && id) {
      lastCompletedRunRef.current = completedRun.id;
      
      // Refresh everything based on what task completed
      fetchDocuments(id);
      fetchClaim(id); // Re-fetch claim for evidence score, analysis, council insights
      fetchPriorReports(id); // Get any new prior reports from check-prior-reports task
      fetchPacks(id); // Get newly generated packs from generate-claim-pack task
    }
  }, [realtimeRuns, id, fetchDocuments, fetchClaim, fetchPriorReports, fetchPacks]);

  // Fallback polling if realtime isn't available
  useEffect(() => {
    // Only poll if we don't have realtime AND have pending docs
    if (realtimeToken || !hasPendingDocs || !id) return;
    
    const pollInterval = setInterval(() => {
      fetchDocuments(id);
      fetchClaim(id); // Also refresh claim data
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [realtimeToken, hasPendingDocs, pendingDocs.length, id, fetchDocuments, fetchClaim]);
  
  // Initial data refresh after claim creation - catches background task completions
  // This runs once after the initial load to pick up data from tasks that started during claim creation
  const [initialRefreshDone, setInitialRefreshDone] = useState(false);
  useEffect(() => {
    if (!id || !currentClaim || initialRefreshDone) return;
    
    // If claim was just created (within last 30 seconds), poll for background task completions
    const claimAge = Date.now() - new Date(currentClaim.createdAt as string).getTime();
    const isNewClaim = claimAge < 30000; // 30 seconds
    
    if (isNewClaim) {
      let pollCount = 0;
      const maxPolls = 10; // Poll for up to 20 seconds (10 x 2s)
      
      const pollInterval = setInterval(async () => {
        pollCount++;
        
        await Promise.all([
          fetchClaim(id),
          fetchPriorReports(id),
          fetchDocuments(id),
        ]);
        
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          setInitialRefreshDone(true);
        }
      }, 2000);
      
      return () => clearInterval(pollInterval);
    } else {
      setInitialRefreshDone(true);
    }
  }, [id, currentClaim?.id, currentClaim?.createdAt, initialRefreshDone, fetchClaim, fetchPriorReports, fetchDocuments]);

  // Count documents pending AI analysis
  const unanalysedCount = documents.filter((doc) => !doc.aiProcessed).length;
  
  // Count rejected documents
  const rejectedCount = documents.filter((doc) => {
    const analysis = doc.aiAnalysis as any;
    return analysis?.rejected || doc.rejected;
  }).length;

  // Upload limits per document type
  const UPLOAD_LIMITS = {
    pothole_photo: 5,
    damage_photo: 5,
    repair_invoice: 3,
    repair_quote: 3, // Shares limit with invoice
    dashcam: 2,
    other: 5,
  };
  
  // Count documents by type (excluding rejected)
  const validDocuments = documents.filter((doc) => {
    const analysis = doc.aiAnalysis as any;
    return !analysis?.rejected && !doc.rejected;
  });
  
  const documentCounts = {
    pothole_photo: validDocuments.filter(d => d.type === 'pothole_photo' || (d.aiAnalysis as any)?.detectedType === 'pothole_photo').length,
    damage_photo: validDocuments.filter(d => d.type === 'damage_photo' || (d.aiAnalysis as any)?.detectedType === 'damage_photo').length,
    invoices: validDocuments.filter(d => ['repair_invoice', 'repair_quote'].includes(d.type) || ['repair_invoice', 'repair_quote'].includes((d.aiAnalysis as any)?.detectedType)).length,
    dashcam: validDocuments.filter(d => d.type === 'dashcam' || (d.aiAnalysis as any)?.detectedType === 'dashcam').length,
  };
  
  // Check if we've hit the total upload limit (sum of all max limits)
  const totalMaxUploads = UPLOAD_LIMITS.pothole_photo + UPLOAD_LIMITS.damage_photo + UPLOAD_LIMITS.repair_invoice + UPLOAD_LIMITS.dashcam + UPLOAD_LIMITS.other;
  const atTotalLimit = validDocuments.length >= totalMaxUploads;

  // Detect over-limit situations and show modal
  useEffect(() => {
    // Don't check if modal is already showing or we're in the middle of deleting
    if (overLimitModal?.show || isDeletingOverLimit) return;
    
    // Check each category for over-limit
    const categoryChecks = [
      { type: 'pothole_photo', name: 'Pothole Photos', limit: UPLOAD_LIMITS.pothole_photo },
      { type: 'damage_photo', name: 'Vehicle Damage Photos', limit: UPLOAD_LIMITS.damage_photo },
      { type: 'repair_invoice', name: 'Repair Documents', limit: UPLOAD_LIMITS.repair_invoice },
      { type: 'dashcam', name: 'Dashcam Footage', limit: UPLOAD_LIMITS.dashcam },
    ];
    
    for (const check of categoryChecks) {
      const docsInCategory = validDocuments.filter(d => {
        const detectedType = (d.aiAnalysis as any)?.detectedType || d.type;
        if (check.type === 'repair_invoice') {
          return ['repair_invoice', 'repair_quote'].includes(detectedType);
        }
        return detectedType === check.type;
      });
      
      if (docsInCategory.length > check.limit) {
        // Find the most recently uploaded document to delete
        const sortedByDate = [...docsInCategory].sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        const docToDelete = sortedByDate[0]; // Most recent
        
        // Skip if we already handled this document (prevents modal loop)
        if (docToDelete?.id && dismissedOverLimitDocsRef.current.has(docToDelete.id)) {
          continue;
        }
        
        setOverLimitModal({
          show: true,
          category: check.type,
          categoryName: check.name,
          maxCount: check.limit,
          docToDelete: docToDelete?.id || null,
        });
        break; // Only show one modal at a time
      }
    }
  }, [validDocuments, overLimitModal?.show, isDeletingOverLimit]);

  // Handle over-limit document deletion
  const handleOverLimitDelete = async () => {
    if (!overLimitModal?.docToDelete) return;
    
    // Mark this doc as dismissed to prevent modal loop
    dismissedOverLimitDocsRef.current.add(overLimitModal.docToDelete);
    
    setIsDeletingOverLimit(true);
    try {
      // Use the store's deleteDocument with over-limit tracking
      await deleteDocument(overLimitModal.docToDelete, {
        reason: 'over_limit',
        category: overLimitModal.category,
      });
    } catch (error) {
      console.error('Failed to delete over-limit document:', error);
    } finally {
      setIsDeletingOverLimit(false);
      setOverLimitModal(null);
    }
  };

  const handleFileUpload = useCallback(async (files: FileList | null) => {
    if (!files || !id) return;
    
    const fileArray = Array.from(files);
    
    // Enforce the advertised 10MB per-file limit client-side
    const oversizedFiles = fileArray.filter((file) => file.size > MAX_FILE_SIZE);
    const validFiles = fileArray.filter((file) => file.size <= MAX_FILE_SIZE);
    
    if (oversizedFiles.length > 0) {
      const names = oversizedFiles
        .map((file) => `"${file.name}" (${(file.size / (1024 * 1024)).toFixed(1)}MB)`)
        .join(', ');
      setUploadError(
        `${oversizedFiles.length === 1 ? 'This file exceeds' : 'These files exceed'} the 10MB limit and ${oversizedFiles.length === 1 ? 'was' : 'were'} not uploaded: ${names}`
      );
      if (validFiles.length === 0) return;
    }
    
    // Check total limit (count uploaded files plus any still in flight)
    if (validDocuments.length + pendingUploads.length + validFiles.length > totalMaxUploads) {
      setUploadError(`Maximum uploads reached. You can upload up to ${totalMaxUploads} files total.`);
      return;
    }

    if (oversizedFiles.length === 0) {
      setUploadError(null);
    }

    // Show an optimistic placeholder card per file so the user gets immediate,
    // per-file feedback while the upload + AI hand-off happens.
    const queued = validFiles.map((file) => {
      const isImage = file.type.startsWith('image/');
      return {
        tempId:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        fileName: file.name,
        isImage,
        previewUrl: isImage ? URL.createObjectURL(file) : undefined,
      };
    });

    setPendingUploads((prev) => [
      ...prev,
      ...queued.map((q) => ({
        tempId: q.tempId,
        fileName: q.fileName,
        isImage: q.isImage,
        previewUrl: q.previewUrl,
        status: 'uploading' as const,
      })),
    ]);
    setIsUploading(true);

    await Promise.all(
      queued.map(async ({ tempId, file, previewUrl }) => {
        try {
          // AI will auto-detect the type
          await uploadEvidenceWithMeasurement(uploadDocument, id, file);
          // Success: the real document is now in the list (with its own
          // "Mac is checking…" state), so drop the placeholder.
          setPendingUploads((prev) => prev.filter((p) => p.tempId !== tempId));
          if (previewUrl) URL.revokeObjectURL(previewUrl);
        } catch (error) {
          setPendingUploads((prev) =>
            prev.map((p) =>
              p.tempId === tempId
                ? {
                    ...p,
                    status: 'error',
                    error: error instanceof Error ? error.message : 'Upload failed',
                  }
                : p
            )
          );
        }
      })
    );

    setIsUploading(false);
  }, [id, uploadDocument, validDocuments.length, pendingUploads.length, totalMaxUploads]);

  const dismissPendingUpload = useCallback((tempId: string) => {
    setPendingUploads((prev) => {
      const target = prev.find((p) => p.tempId === tempId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.tempId !== tempId);
    });
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  const handleSearchPriorReports = async () => {
    if (!id) return { found: 0, reportUrl: null };
    
    setIsSearchingReports(true);
    try {
      const result = await searchPriorReports(id);
      return { found: result.found, reportUrl: result.reportUrl };
    } finally {
      setIsSearchingReports(false);
    }
  };

  const handleConfirmPriorReport = async (reportId: string) => {
    if (!id) return;
    await confirmPriorReport(id, reportId);
    // Also trigger an explicit refresh after a short delay to catch the updated score
    setTimeout(() => {
      fetchClaim(id);
    }, 5000);
  };

  const handleRemovePriorReport = async (reportId: string) => {
    if (!id) return;
    await removePriorReport(id, reportId);
  };

  // Only show skeleton on INITIAL load (no claim yet)
  // Keep showing current content during refreshes
  if (!currentClaim) {
    // The fetch failed (error / not found): show a message instead of an infinite skeleton
    if (claimError && !isLoading) {
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/claims"
            className="inline-flex items-center text-sm text-surface-800 hover:text-brand-600 mb-6"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to claims
          </Link>
          <div className="bg-white rounded-2xl border border-surface-200 p-12 text-center">
            <div className="w-16 h-16 bg-error-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h1 className="font-display text-2xl font-bold text-brand-950 mb-2">
              We couldn't load this claim
            </h1>
            <p className="text-surface-600 mb-2 max-w-md mx-auto">
              {claimError}
            </p>
            <p className="text-sm text-surface-500 mb-6 max-w-md mx-auto">
              The claim may not exist, or there may have been a connection problem.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => id && fetchClaim(id)}
                className="inline-flex items-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
              >
                Retry
              </button>
              <Link
                to="/claims"
                className="inline-flex items-center px-6 py-3 bg-white text-brand-700 font-semibold rounded-xl border border-surface-300 hover:border-brand-300 transition-colors"
              >
                Back to claims
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-surface-200 rounded w-1/3 mb-4" />
          <div className="h-4 bg-surface-200 rounded w-1/2 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="h-48 bg-surface-200 rounded-2xl" />
              <div className="h-64 bg-surface-200 rounded-2xl" />
            </div>
            <div className="space-y-6">
              <div className="h-32 bg-surface-200 rounded-2xl" />
              <div className="h-48 bg-surface-200 rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <Link
            to="/claims"
            className="inline-flex items-center text-sm text-surface-800 hover:text-brand-600 mb-4"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to claims
          </Link>
          <div>
            <p className="text-xs font-medium text-surface-500 uppercase tracking-wide mb-1">
              📍 Location of pothole
            </p>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl font-bold text-brand-950">
                {currentClaim.locationAddress || 'Claim Details'}
              </h1>
            </div>
            {isLoading && (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-brand-50 text-brand-600 text-xs font-medium rounded-full animate-pulse">
                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Updating...
              </span>
            )}
          </div>
          <p className="mt-2 text-surface-800">
            Incident date: {new Date(currentClaim.incidentDate).toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>
        <span
          className={clsx(
            'px-4 py-2 rounded-xl text-sm font-medium',
            currentClaim.status === 'draft'
              ? 'bg-surface-100 text-surface-800'
              : currentClaim.status === 'pack_generated'
              ? 'bg-success-500/10 text-success-600'
              : 'bg-brand-100 text-brand-700'
          )}
        >
          {currentClaim.status.replace('_', ' ')}
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Upload Error */}
          {uploadError && (
            <div className="bg-error-50 border border-error-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <span className="text-error-500 text-xl">⚠️</span>
                <div className="flex-1">
                  <p className="font-medium text-error-800">Upload Failed</p>
                  <p className="text-sm text-error-700 mt-1">{uploadError}</p>
                </div>
                <button 
                  onClick={() => setUploadError(null)}
                  className="text-error-400 hover:text-error-600"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          
          {/* Payment Message */}
          {paymentMessage && (
            <div className={clsx(
              'rounded-xl p-4 border',
              paymentMessage.type === 'success' 
                ? 'bg-success-50 border-success-200' 
                : 'bg-surface-50 border-surface-200'
            )}>
              <div className="flex items-start gap-3">
                <span className="text-xl">
                  {paymentMessage.type === 'success' ? '🎉' : 'ℹ️'}
                </span>
                <div className="flex-1">
                  <p className={clsx(
                    'font-medium',
                    paymentMessage.type === 'success' ? 'text-success-800' : 'text-surface-800'
                  )}>
                    {paymentMessage.text}
                  </p>
                </div>
                <button
                  onClick={() => setPaymentMessage(null)}
                  className="text-surface-400 hover:text-surface-600"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Claim Packs Download Section */}
          <PackDownloadList
            packs={packs}
            isGenerating={isPackGenerating}
            dedupeScope={currentClaim.id}
          />

          {/* Evidence Score - Using the improved component */}
          {currentClaim.evidenceScore != null && (
            <EvidenceScore 
              score={currentClaim.evidenceScore} 
              analysis={currentClaim.evidenceAnalysis}
              dedupeScope={currentClaim.id}
            />
          )}

          {/* Prior Reports Section */}
          <PriorReportsSection
            reports={priorReports}
            hasLocation={!!(currentClaim.locationLat && currentClaim.locationLng)}
            hasPostcode={!!currentClaim.locationPostcode}
            isSearching={isSearchingReports}
            searchCompleted={!!(currentClaim.locationLat && currentClaim.locationLng)}
            onSearch={handleSearchPriorReports}
            onConfirm={handleConfirmPriorReport}
            onUnlink={handleRemovePriorReport}
            readOnly={isPackGenerated}
          />

          {/* Evidence Checklist - Guides user through what's needed */}
          <EvidenceChecklist 
            documents={documents}
            onCategoryClick={setActiveEvidenceCategory}
            activeCategory={activeEvidenceCategory}
          />

          {/* Upload Section - Hidden once pack is generated */}
          {!isPackGenerated && (
          <div className="bg-white rounded-2xl border border-surface-200 p-6">
            <div className="mb-6">
              <h2 className="font-display text-lg font-semibold text-brand-950">
                Upload Evidence
              </h2>
              <p className="text-sm text-surface-600 mt-1">
                Mac automatically identifies and analyses each document you upload
              </p>
            </div>

            {/* Upload area - file input overlays entire area for direct clicking */}
            {atTotalLimit ? (
              <div className="border-2 border-dashed rounded-xl p-8 text-center border-surface-200 bg-surface-50">
                <div className="w-16 h-16 bg-surface-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-semibold text-surface-600 text-lg">
                  Maximum uploads reached
                </p>
                <p className="text-sm text-surface-500 mt-2">
                  You've uploaded the maximum allowed evidence files.
                </p>
                <p className="text-xs text-surface-400 mt-1">
                  Delete existing files if you need to replace them.
                </p>
              </div>
            ) : (
              <div
                className={clsx(
                  'relative border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer',
                  dragOver
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-surface-300 hover:border-brand-400'
                )}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                {/* Visual content - pointer-events-none so clicks pass through to file input */}
                <div className="pointer-events-none">
                  <div className="w-16 h-16 bg-gradient-to-br from-brand-100 to-brand-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="font-semibold text-brand-950 text-lg">
                    {isUploading ? 'Uploading...' : 'Drop files here or click to upload'}
                  </p>
                  <p className="text-sm text-surface-700 mt-2 max-w-md mx-auto">
                    We need <span className="font-semibold text-brand-700">photos of the pothole</span> and{' '}
                    <span className="font-semibold text-brand-700">the damage</span>, plus a{' '}
                    <span className="font-semibold text-brand-700">repair invoice or quote</span>.
                  </p>
                  <p className="text-xs text-surface-500 mt-1 max-w-md mx-auto">
                    Screenshots of receipts, shopping orders or web pages can&apos;t be used as evidence.
                  </p>
                  <div className="mt-3 space-y-1">
                    <p className="text-sm text-surface-600">
                      <span className="font-medium text-brand-700">Photos:</span> JPG, PNG, HEIC, WebP
                    </p>
                    <p className="text-sm text-surface-600">
                      <span className="font-medium text-brand-700">Documents:</span> PDF, Word, Excel
                    </p>
                    <p className="text-sm text-surface-600">
                      <span className="font-medium text-brand-700">Videos:</span> MP4, MOV, AVI, WebM
                    </p>
                  </div>
                  <p className="text-xs text-surface-400 mt-2">
                    Maximum 10MB per file
                  </p>
                </div>
                {/* File input AFTER content - using inline style for Chrome compatibility */}
                <input
                  type="file"
                  multiple
                  aria-label="Upload evidence files"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.mp4,.mov,.avi,.webm,.mkv"
                  onChange={(e) => {
                    handleFileUpload(e.target.files);
                    e.target.value = '';
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    fontSize: '0',
                  }}
                />
              </div>
            )}
            
            {/* Quick upload buttons - each with overlaid file input */}
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {/* Pothole photo button */}
              <div 
                className={clsx(
                  "relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors",
                  documentCounts.pothole_photo >= UPLOAD_LIMITS.pothole_photo
                    ? "bg-surface-50 text-surface-400 cursor-not-allowed"
                    : "bg-surface-100 hover:bg-surface-200 text-surface-700 cursor-pointer"
                )}
                title={documentCounts.pothole_photo >= UPLOAD_LIMITS.pothole_photo 
                  ? `Maximum ${UPLOAD_LIMITS.pothole_photo} pothole photos allowed` 
                  : `${documentCounts.pothole_photo}/${UPLOAD_LIMITS.pothole_photo} uploaded`}
              >
                <span className="pointer-events-none">🕳️</span>
                <span className="pointer-events-none">Pothole</span>
                <span className="pointer-events-none text-xs text-surface-500">
                  ({documentCounts.pothole_photo}/{UPLOAD_LIMITS.pothole_photo})
                </span>
                {documentCounts.pothole_photo < UPLOAD_LIMITS.pothole_photo && (
                  <input
                    type="file"
                    aria-label="Upload pothole photo"
                    accept="image/*"
                    onChange={(e) => {
                      handleFileUpload(e.target.files);
                      e.target.value = '';
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                      fontSize: '0',
                    }}
                  />
                )}
              </div>
              
              {/* Damage photo button */}
              <div 
                className={clsx(
                  "relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors",
                  documentCounts.damage_photo >= UPLOAD_LIMITS.damage_photo
                    ? "bg-surface-50 text-surface-400 cursor-not-allowed"
                    : "bg-surface-100 hover:bg-surface-200 text-surface-700 cursor-pointer"
                )}
                title={documentCounts.damage_photo >= UPLOAD_LIMITS.damage_photo 
                  ? `Maximum ${UPLOAD_LIMITS.damage_photo} damage photos allowed` 
                  : `${documentCounts.damage_photo}/${UPLOAD_LIMITS.damage_photo} uploaded`}
              >
                <span className="pointer-events-none">🚗</span>
                <span className="pointer-events-none">Damage</span>
                <span className="pointer-events-none text-xs text-surface-500">
                  ({documentCounts.damage_photo}/{UPLOAD_LIMITS.damage_photo})
                </span>
                {documentCounts.damage_photo < UPLOAD_LIMITS.damage_photo && (
                  <input
                    type="file"
                    aria-label="Upload vehicle damage photo"
                    accept="image/*"
                    onChange={(e) => {
                      handleFileUpload(e.target.files);
                      e.target.value = '';
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                      fontSize: '0',
                    }}
                  />
                )}
              </div>
              
              {/* Invoice/Quote button */}
              <div 
                className={clsx(
                  "relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors",
                  documentCounts.invoices >= UPLOAD_LIMITS.repair_invoice
                    ? "bg-surface-50 text-surface-400 cursor-not-allowed"
                    : "bg-surface-100 hover:bg-surface-200 text-surface-700 cursor-pointer"
                )}
                title={documentCounts.invoices >= UPLOAD_LIMITS.repair_invoice 
                  ? `Maximum ${UPLOAD_LIMITS.repair_invoice} invoices/quotes allowed` 
                  : `${documentCounts.invoices}/${UPLOAD_LIMITS.repair_invoice} uploaded`}
              >
                <span className="pointer-events-none">🧾</span>
                <span className="pointer-events-none">Invoice</span>
                <span className="pointer-events-none text-xs text-surface-500">
                  ({documentCounts.invoices}/{UPLOAD_LIMITS.repair_invoice})
                </span>
                {documentCounts.invoices < UPLOAD_LIMITS.repair_invoice && (
                  <input
                    type="file"
                    aria-label="Upload repair invoice or quote"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      handleFileUpload(e.target.files);
                      e.target.value = '';
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                      fontSize: '0',
                    }}
                  />
                )}
              </div>
              
              {/* Dashcam button */}
              <div 
                className={clsx(
                  "relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors",
                  documentCounts.dashcam >= UPLOAD_LIMITS.dashcam
                    ? "bg-surface-50 text-surface-400 cursor-not-allowed"
                    : "bg-surface-100 hover:bg-surface-200 text-surface-700 cursor-pointer"
                )}
                title={documentCounts.dashcam >= UPLOAD_LIMITS.dashcam 
                  ? `Maximum ${UPLOAD_LIMITS.dashcam} dashcam files allowed` 
                  : `${documentCounts.dashcam}/${UPLOAD_LIMITS.dashcam} uploaded`}
              >
                <span className="pointer-events-none">📹</span>
                <span className="pointer-events-none">Dashcam</span>
                <span className="pointer-events-none text-xs text-surface-500">
                  ({documentCounts.dashcam}/{UPLOAD_LIMITS.dashcam})
                </span>
                {documentCounts.dashcam < UPLOAD_LIMITS.dashcam && (
                  <input
                    type="file"
                    aria-label="Upload dashcam footage"
                    accept="video/*"
                    onChange={(e) => {
                      handleFileUpload(e.target.files);
                      e.target.value = '';
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                      fontSize: '0',
                    }}
                  />
                )}
              </div>
            </div>
            
          </div>
          )}

          {/* Uploaded Documents List */}
          {(documents.length > 0 || pendingUploads.length > 0) && (
            <div className="bg-white rounded-2xl border border-surface-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-semibold text-brand-950">
                  Your Evidence ({documents.length + pendingUploads.length} item{documents.length + pendingUploads.length !== 1 ? 's' : ''})
                </h2>
                {(documents.some(d => !d.aiProcessed) || pendingUploads.some(p => p.status === 'uploading')) && (
                  <span className="text-xs text-brand-600 flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500"></span>
                    </span>
                    {pendingUploads.some(p => p.status === 'uploading') ? 'Uploading...' : 'Check in progress...'}
                  </span>
                )}
              </div>

              <ul className="space-y-4">
                {/* Optimistic placeholders for in-flight uploads */}
                {pendingUploads.map((p) => (
                  <li
                    key={p.tempId}
                    className={clsx(
                      'p-4 rounded-xl border-2',
                      p.status === 'error'
                        ? 'bg-error-50 border-error-300'
                        : 'bg-brand-50/60 border-brand-200 border-dashed'
                    )}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-surface-100 flex items-center justify-center flex-shrink-0">
                        {p.isImage && p.previewUrl ? (
                          <img
                            src={p.previewUrl}
                            alt={p.fileName}
                            className={clsx('w-full h-full object-cover', p.status === 'uploading' && 'opacity-70')}
                          />
                        ) : (
                          <span className="text-2xl">📄</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-brand-950 truncate">{p.fileName}</p>
                        {p.status === 'uploading' ? (
                          <div className="flex items-center gap-2 mt-1">
                            <svg className="animate-spin h-4 w-4 text-brand-600" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span className="text-xs text-brand-600">Uploading&hellip;</span>
                          </div>
                        ) : (
                          <div className="mt-1">
                            <p className="text-sm text-error-600 font-medium">Upload failed</p>
                            {p.error && <p className="text-xs text-error-500 mt-0.5">{p.error}</p>}
                          </div>
                        )}
                      </div>
                      {p.status === 'error' && (
                        <button
                          type="button"
                          onClick={() => dismissPendingUpload(p.tempId)}
                          className="text-error-400 hover:text-error-600 flex-shrink-0"
                          aria-label="Dismiss failed upload"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </li>
                ))}
                {documents.map((doc) => {
                  const analysis = doc.aiAnalysis as any;
                  const extracted = analysis?.extracted;
                  const quality = analysis?.quality;
                  const isRejected = analysis?.rejected || doc.rejected;
                  
                  // Rejected document card
                  if (isRejected) {
                    return (
                      <li
                        key={doc.id}
                        className="p-4 bg-error-50 rounded-xl border-2 border-error-300"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4 flex-1">
                            {/* Thumbnail preview with red overlay */}
                            <div className="relative">
                              {doc.fileUrl && doc.mimeType?.startsWith('image/') ? (
                                <img 
                                  src={doc.fileUrl} 
                                  alt={doc.originalName || 'Document'} 
                                  className="w-16 h-16 rounded-lg object-cover bg-surface-200 opacity-60"
                                />
                              ) : (
                                <div className="w-16 h-16 bg-error-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <span className="text-2xl">❌</span>
                                </div>
                              )}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-2xl">🚫</span>
                              </div>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-base">⚠️</span>
                                <p className="font-medium text-error-700">
                                  Invalid Evidence
                                </p>
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-error-100 text-error-700 ml-auto">
                                  Rejected
                                </span>
                              </div>
                              
                              <p className="text-sm text-error-600 font-medium mb-1">
                                This image cannot be used as evidence for your claim.
                              </p>
                              
                              {analysis?.detectedContent && (
                                <p className="text-sm text-surface-600 mb-1">
                                  <span className="font-medium">Detected:</span> {analysis.detectedContent}
                                </p>
                              )}
                              
                              {analysis?.rejectionReason && (
                                <p className="text-sm text-error-600">
                                  {analysis.rejectionReason}
                                </p>
                              )}
                              
                              <div className="mt-2 rounded-lg bg-white border border-error-200 p-2.5">
                                <p className="text-xs font-semibold text-surface-700 mb-1">
                                  Upload one of these instead:
                                </p>
                                <ul className="text-xs text-surface-600 space-y-0.5">
                                  <li>📷 A photo of the pothole (a coin or your foot in shot for scale)</li>
                                  <li>🚗 A photo of the damage to your vehicle</li>
                                  <li>🧾 A repair invoice or written quote from a garage</li>
                                </ul>
                              </div>
                              
                              <p className="text-xs text-surface-400 mt-2 truncate">{doc.originalName}</p>
                            </div>
                          </div>
                          
                          {!isPackGenerated && (
                            <button
                              onClick={() => deleteDocument(doc.id)}
                              className="p-2 text-error-500 hover:text-error-700 transition-colors ml-2"
                              title="Remove this file"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  }
                  
                  return (
                    <li
                      key={doc.id}
                      className="p-4 bg-surface-50 rounded-xl border border-surface-200"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          {/* Thumbnail preview */}
                          {doc.fileUrl && doc.mimeType?.startsWith('image/') ? (
                            <img 
                              src={doc.fileUrl} 
                              alt={doc.originalName || 'Document'} 
                              className="w-16 h-16 rounded-lg object-cover bg-surface-200"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-2xl">
                                {doc.type === 'repair_invoice' || doc.type === 'repair_quote' ? '🧾' : '📄'}
                              </span>
                            </div>
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-base">{getDocumentIcon(analysis?.detectedType || doc.type)}</span>
                              <p className="font-medium text-brand-950">
                                {formatDocumentType(analysis?.detectedType || doc.type)}
                              </p>
                              {quality?.score && (
                                <span 
                                  className={clsx(
                                    'text-xs font-medium px-2 py-0.5 rounded-full ml-auto cursor-help',
                                    quality.score >= 80 ? 'bg-success-100 text-success-700' :
                                    quality.score >= 60 ? 'bg-warning-100 text-warning-700' :
                                    'bg-error-100 text-error-700'
                                  )}
                                  title="Photo Quality Score: Measures clarity, focus, lighting, and completeness of the photograph as evidence. Higher scores indicate better quality photos that will strengthen your claim."
                                >
                                  {quality.score}/100
                                </span>
                              )}
                            </div>
                            
                            {/* AI Description */}
                            {doc.aiProcessed && extracted?.description && (
                              <p className="text-sm text-surface-700 leading-relaxed">
                                {extracted.description}
                              </p>
                            )}
                            
                            {/* Surrounding Context (for pothole photos) */}
                            {doc.aiProcessed && extracted?.surroundingContext && (
                              <p className="text-xs text-surface-500 mt-1">
                                📍 {extracted.surroundingContext}
                              </p>
                            )}
                            
                            {/* Key extracted data - compact format */}
                            {doc.aiProcessed && extracted && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {/* Pothole measurements */}
                                {extracted.estimatedDepth && (
                                  <span className="text-xs bg-surface-100 text-surface-700 px-2 py-0.5 rounded">
                                    Depth: {extracted.estimatedDepth}
                                  </span>
                                )}
                                {extracted.estimatedWidth && (
                                  <span className="text-xs bg-surface-100 text-surface-700 px-2 py-0.5 rounded">
                                    Width: {extracted.estimatedWidth}
                                  </span>
                                )}
                                {extracted.measurementVisible !== undefined && (
                                  <span className={clsx(
                                    'text-xs px-2 py-0.5 rounded',
                                    extracted.measurementVisible 
                                      ? 'bg-success-100 text-success-700' 
                                      : 'bg-amber-100 text-amber-700'
                                  )}>
                                    {extracted.measurementVisible ? '✓ Scale reference' : 'No scale reference'}
                                  </span>
                                )}
                                
                                {/* Damage info */}
                                {extracted.severity && (
                                  <span className="text-xs bg-surface-100 text-surface-700 px-2 py-0.5 rounded">
                                    {extracted.severity} severity
                                  </span>
                                )}
                                {extracted.vehiclePart && (
                                  <span className="text-xs bg-surface-100 text-surface-700 px-2 py-0.5 rounded">
                                    {extracted.vehiclePart}
                                  </span>
                                )}
                                
                                {/* Invoice info */}
                                {extracted.totalAmount && (
                                  <span className="text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded font-medium">
                                    £{extracted.totalAmount.toFixed(2)}
                                  </span>
                                )}
                                {extracted.vendorName && (
                                  <span className="text-xs bg-surface-100 text-surface-700 px-2 py-0.5 rounded">
                                    {extracted.vendorName}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Quality suggestions */}
                            {doc.aiProcessed && quality?.suggestions?.length > 0 && (
                              <p className="text-xs text-amber-600 mt-2">
                                💡 {quality.suggestions[0]}
                              </p>
                            )}
                            
                            {/* Analysing state */}
                            {!doc.aiProcessed && (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="relative flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500"></span>
                                </span>
                                <span className="text-xs text-brand-600 animate-pulse">Mac is checking...</span>
                              </div>
                            )}
                            
                            {/* File name - subtle */}
                            <p className="text-xs text-surface-400 mt-2 truncate">{doc.originalName}</p>
                          </div>
                        </div>
                        
                        {!isPackGenerated && (
                          <button
                            onClick={() => deleteDocument(doc.id)}
                            className="p-2 text-surface-400 hover:text-error-600 transition-colors ml-2"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Details */}
          <div className="bg-white rounded-2xl border border-surface-200 p-6">
            <h3 className="font-display text-lg font-semibold text-brand-950 mb-4">
              Your Details
            </h3>
            <dl className="space-y-3">
              {/* Full Name */}
              <div>
                <dt className="text-xs text-surface-500 uppercase tracking-wide">Full Name</dt>
                <dd className="text-sm font-medium text-brand-950 mt-0.5">
                  {currentClaim.customerForename && currentClaim.customerSurname
                    ? `${currentClaim.customerPrefix ? currentClaim.customerPrefix + ' ' : ''}${currentClaim.customerForename} ${currentClaim.customerSurname}`
                    : <span className="text-surface-400 italic">Not provided</span>
                  }
                </dd>
              </div>
              
              {/* Address */}
              <div>
                <dt className="text-xs text-surface-500 uppercase tracking-wide">Address</dt>
                <dd className="text-sm text-brand-950 mt-0.5">
                  {currentClaim.customerAddressLine1 ? (
                    <div className="space-y-0.5">
                      <p>{currentClaim.customerAddressLine1}</p>
                      {currentClaim.customerAddressLine2 && <p>{currentClaim.customerAddressLine2}</p>}
                      <p>
                        {[currentClaim.customerCity, currentClaim.customerPostcode]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>
                  ) : (
                    <span className="text-surface-400 italic">Not provided</span>
                  )}
                </dd>
              </div>
              
              {/* Email */}
              <div>
                <dt className="text-xs text-surface-500 uppercase tracking-wide">Email</dt>
                <dd className="text-sm text-brand-950 mt-0.5">
                  {useAuthStore.getState().user?.email || <span className="text-surface-400 italic">Not available</span>}
                </dd>
              </div>
            </dl>
          </div>
          
          {/* Evidence Analysis Status */}
          <div className="bg-white rounded-2xl border border-surface-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-brand-950">
                Analysis Status
              </h3>
              {unanalysedCount > 0 && (
                <span className="flex items-center gap-1.5 text-xs text-brand-600">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500"></span>
                  </span>
                  Processing...
                </span>
              )}
            </div>
            
            <div className="space-y-4">
              {/* Processing Status */}
              {unanalysedCount > 0 ? (
                <div className="p-4 bg-brand-50 rounded-xl border border-brand-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-brand-600 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-brand-900">Check in progress</p>
                      <p className="text-xs text-brand-700">{unanalysedCount} item{unanalysedCount !== 1 ? 's' : ''} being processed</p>
                    </div>
                  </div>
                  <p className="text-xs text-brand-600">
                    Your evidence score will update automatically when complete.
                  </p>
                </div>
              ) : documents.length > 0 ? (
                <div className="p-4 bg-success-50 rounded-xl border border-success-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-success-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-success-900">All evidence analysed</p>
                      <p className="text-xs text-success-700">{documents.length} item{documents.length !== 1 ? 's' : ''} processed</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-surface-50 rounded-xl border border-surface-200">
                  <p className="text-sm text-surface-600 text-center">
                    Upload evidence to begin automatic analysis
                  </p>
                </div>
              )}
              
              {/* Rejected documents warning */}
              {rejectedCount > 0 && (
                <div className="p-4 bg-error-50 rounded-xl border border-error-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-error-100 rounded-lg flex items-center justify-center">
                      <span className="text-lg">⚠️</span>
                    </div>
                    <div>
                      <p className="font-medium text-error-900">Invalid uploads detected</p>
                      <p className="text-xs text-error-700">
                        {rejectedCount} file{rejectedCount !== 1 ? 's' : ''} rejected — not relevant evidence
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-error-600 mt-2">
                    Remove invalid files from the evidence list above.
                  </p>
                </div>
              )}
              
            </div>
          </div>
          
          {/* Get Your Claim Pack */}
          <div className="bg-white rounded-2xl border border-surface-200 p-6">
            <h3 className="font-display text-lg font-semibold text-brand-950 mb-2">
              Get Your Claim Pack
            </h3>
            <p className="text-sm text-surface-600 mb-4">
              Generate a professional claim document to send to the council.
            </p>
            <div className="space-y-3">
              <PurchasePackButton
                claimId={currentClaim.id}
                packType="council"
                evidenceScore={currentClaim.evidenceScore ?? undefined}
                alreadyPurchased={packs.some(p => p.packType === 'council')}
                hasAuthorityAddress={!!(currentClaim.authorityAddress || currentClaim.authority?.contactAddress)}
                disabled={isPackGenerating || unanalysedCount > 0}
              />
              
              {/* Court Pack - Phase 4 feature, hidden for now */}
              
              {unanalysedCount > 0 && (
                <p className="text-xs text-amber-600 text-center">
                  Wait for analysis to complete before generating pack
                </p>
              )}
            </div>
          </div>

          {/* Incident Description - Most important for the claim narrative */}
          {currentClaim.damageDescription && (
            <IncidentDescriptionCard 
              claimId={currentClaim.id}
              description={currentClaim.damageDescription}
              damageTypes={currentClaim.damageType}
              incidentDate={currentClaim.incidentDate as string}
              cachedAnalysis={(currentClaim as any).descriptionAnalysis as DescriptionAnalysis | null}
              onUpdate={async (newDescription) => {
                await updateClaim(currentClaim.id, { damageDescription: newDescription });
                if (id) fetchClaim(id);
              }}
              readOnly={isPackGenerated}
            />
          )}

          {/* Claim details */}
          <div className="bg-white rounded-2xl border border-surface-200 p-6">
            <h3 className="font-display text-lg font-semibold text-brand-950 mb-4">
              Claim Details
            </h3>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-surface-800">Location</dt>
                <dd className="font-medium text-brand-950">
                  {currentClaim.locationAddress}
                </dd>
                <dd className="text-sm text-surface-800">
                  {currentClaim.locationPostcode}
                </dd>
              </div>
              {currentClaim.damageType && (
                <div>
                  <dt className="text-sm text-surface-800">Damage Type</dt>
                  <dd className="flex flex-wrap gap-1 mt-1">
                    {currentClaim.damageType.map((type) => (
                      <span
                        key={type}
                        className="px-2 py-0.5 bg-surface-100 text-surface-800 text-xs rounded"
                      >
                        {formatDamageType(type)}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
              {currentClaim.repairCost && (
                <div>
                  <dt className="text-sm text-surface-800">Repair Cost</dt>
                  <dd className="font-medium text-brand-950">
                    £{Number(currentClaim.repairCost).toFixed(2)}
                  </dd>
                </div>
              )}
              {currentClaim.vehicleReg && (
                <div>
                  <dt className="text-sm text-surface-800">Vehicle</dt>
                  <dd className="font-medium text-brand-950">
                    {currentClaim.vehicleReg}
                  </dd>
                  <dd className="text-sm text-surface-800">
                    {currentClaim.vehicleMake} {currentClaim.vehicleModel}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Highway Authority */}
          <HighwayAuthorityCard 
            claim={currentClaim} 
            onIdentify={() => identifyAuthority(currentClaim.id)}
            onUpdateAddress={async (address) => {
              await updateClaim(currentClaim.id, { authorityAddress: address });
            }}
            readOnly={isPackGenerated}
          />

          {/* Claim Timeline */}
          <div className="bg-white rounded-2xl border border-surface-200 p-6">
            <h3 className="font-display text-lg font-semibold text-brand-950 mb-4">
              Claim Progress
            </h3>
            <ClaimTimeline
              claimStatus={currentClaim.status}
              createdAt={currentClaim.createdAt as string}
              submittedAt={currentClaim.submittedAt as string | null}
              councilResponseAt={currentClaim.councilResponseAt as string | null}
              outcome={currentClaim.outcome as string | null}
            />
          </div>

          {/* Outcome Tracking */}
          {(currentClaim.status === 'pack_generated' || 
            currentClaim.status === 'submitted' || 
            currentClaim.status === 'awaiting_response') && (
            <OutcomeForm
              claimId={currentClaim.id}
              currentStatus={currentClaim.status}
              onSuccess={() => {
                if (id) fetchClaim(id);
              }}
            />
          )}

          {/* Success Share */}
          {currentClaim.status === 'resolved' && 
           currentClaim.outcome === 'accepted' && 
           currentClaim.compensationAmount && (
            <ShareSuccess
              amount={`£${Number(currentClaim.compensationAmount).toFixed(0)}`}
              council={currentClaim.authority?.name || 'your council'}
              claimId={currentClaim.id}
            />
          )}
        </div>
      </div>

      {/* Over-Limit Modal */}
      {overLimitModal?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-brand-950 mb-2">
                Upload Limit Reached
              </h3>
              <p className="text-surface-600 mb-6">
                You've uploaded more than the maximum {overLimitModal.maxCount} files for <strong>{overLimitModal.categoryName}</strong>.
                The most recent upload will be removed to stay within the limit.
              </p>
              <button
                onClick={handleOverLimitDelete}
                disabled={isDeletingOverLimit}
                className="w-full px-6 py-3 bg-brand-600 text-white rounded-xl font-medium hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                {isDeletingOverLimit ? 'Removing...' : 'OK, Got It'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
